import { useState, useRef, useEffect, useMemo } from 'react'

// Multi-select column filter: a funnel in a table header that opens a list with
// a search box and checkboxes. `values` is the array of chosen values (empty =
// no filter). `options` is an array of strings or { value, label } objects.
export default function ColumnFilterMulti({ label, values, options, onChange, searchPlaceholder = 'Buscar…', clearLabel = 'Limpiar', emptyLabel = 'Sin opciones' }) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const ref = useRef(null)
  const active = values.length > 0
  const norm = useMemo(() => options.map(o => (typeof o === 'string' ? { value: o, label: o } : o)), [options])
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase()
    return s ? norm.filter(o => o.label.toLowerCase().includes(s)) : norm
  }, [norm, q])

  useEffect(() => {
    if (!open) return
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey) }
  }, [open])

  const toggle = (v) => onChange(values.includes(v) ? values.filter(x => x !== v) : [...values, v])

  return (
    <span ref={ref} className="relative inline-flex align-middle normal-case tracking-normal">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); setQ('') }}
        className={`ml-1 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-0.5 rounded text-[10px] font-semibold transition-colors ${
          active ? 'bg-gray-900 text-white' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
        }`}
        title={active ? `${label}: ${values.length}` : label}
        aria-label={label}
        aria-expanded={open}
      >
        <svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor"><path d="M1.5 2.5h13L9.5 9.2v4.3l-3 1.5V9.2L1.5 2.5z" /></svg>
        {active && <span className="ml-0.5">{values.length}</span>}
      </button>
      {open && (
        <div className="absolute z-30 top-6 left-0 w-60 bg-white border border-gray-200 rounded-lg shadow-lg text-left" onClick={e => e.stopPropagation()}>
          <div className="p-2 border-b border-gray-100">
            <input
              autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder={searchPlaceholder} aria-label={searchPlaceholder}
              className="w-full border border-gray-200 rounded-md px-2 py-1 text-xs font-normal text-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-200"
            />
          </div>
          <div className="max-h-60 overflow-auto py-1">
            {shown.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">{emptyLabel}</div>}
            {shown.map(o => (
              <label key={o.value} className="flex items-center gap-2 px-3 py-1.5 text-xs font-normal text-gray-700 hover:bg-gray-50 cursor-pointer">
                <input type="checkbox" className="accent-gray-900" checked={values.includes(o.value)} onChange={() => toggle(o.value)} />
                <span className="truncate" title={o.label}>{o.label}</span>
              </label>
            ))}
          </div>
          {active && (
            <div className="border-t border-gray-100 p-1.5">
              <button type="button" onClick={() => onChange([])} className="w-full text-xs text-gray-600 hover:text-gray-900 py-1">{clearLabel}</button>
            </div>
          )}
        </div>
      )}
    </span>
  )
}
